import { useLocale } from "@/contexts/LocaleContext";
import { copyFor } from "@shared/bilingualContent";
import { projects } from "@shared/siteContent";
import { ArrowUpRight, Crosshair, Layers3, Route, Satellite } from "lucide-react";

const visuals = {
  terrain: "/manus-storage/qm-project-lidar-card_b3a0cd1d.webp",
  corridor: "/manus-storage/qm-project-navigation-card_837cc2c1.webp",
  urban: "/manus-storage/qm-project-satellite-card_2628501a.webp",
} as const;
const pointClouds = Array.from({ length: 30 }, (_, index) => ({
  left: `${8 + ((index * 29) % 84)}%`,
  top: `${16 + ((index * 47) % 68)}%`,
  delay: `${(index % 7) * 80}ms`,
}));

export function SelectedProjects() {
  const { locale } = useLocale();
  const c = copyFor("projects", locale);

  return (
    <section id="selected-projects" className="section-space border-y border-white/10 bg-[#0c0f18]">
      <div className="content-width">
        <div className="grid gap-7 lg:grid-cols-[.66fr_1.34fr] lg:items-end">
          <div>
            <p className="eyebrow">{c.eyebrow}</p>
            <h2 className="display-md mt-6">{c.title}</h2>
          </div>
          <div className="max-w-2xl lg:justify-self-end">
            <p className="leading-7 text-slate-300">{c.summary}</p>
            <p className="mt-4 max-w-xl text-sm leading-6 text-slate-500">{c.evidenceNote}</p>
          </div>
        </div>

        <div className="mt-12 grid gap-5 lg:grid-cols-3">
          {projects.map((project, index) => {
            const copy = c.items[index];
            return (
              <article key={project.slug} className="project-card group">
                <div className="project-card-media media-frame aspect-[4/3]">
                  <img src={visuals[project.visual]} alt={c.visualAlt} loading="lazy" className="image-treatment h-full w-full object-cover" />
                  <ProjectSignal type={project.visual} />
                  <div className="absolute inset-x-4 top-4 flex items-start justify-between gap-3">
                    <span className="placeholder-tag">{c.status}</span>
                    <span className="project-number">{project.number}</span>
                  </div>
                  <div className="project-media-caption">{project.visual === "terrain" ? "LiDAR / 3D" : project.visual === "corridor" ? "GNSS/INS / Mobile" : "EO / GIS"}</div>
                </div>

                <div className="p-6 sm:p-7">
                  <p className="mono-label text-cyan-200">{copy.discipline}</p>
                  <h3 className="mt-5 text-2xl font-semibold leading-tight text-white">{copy.title}</h3>
                  <p className="mt-4 text-sm leading-6 text-slate-400">{copy.summary}</p>

                  <div className="mt-6 grid gap-2 border-y border-white/10 py-4 text-xs">
                    <MetaRow label={c.areaLabel} value={project.area} />
                    <MetaRow label={c.platformLabel} value={project.platform} />
                    <MetaRow label={c.sensorLabel} value={project.sensor} />
                    <MetaRow label={c.positioningLabel} value={project.positioning} />
                  </div>

                  <div className="mt-6">
                    <p className="mono-label">{c.workflowLabel}</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {project.workflow.map((step, stepIndex) => <span key={step} className="project-step"><span>{String(stepIndex + 1).padStart(2, "0")}</span>{step}</span>)}
                    </div>
                  </div>

                  <div className="mt-6">
                    <p className="mono-label">{c.outputsLabel}</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {project.deliverables.map(output => <span key={output} className="project-output">{output}</span>)}
                    </div>
                  </div>

                  <div className="mt-6 space-y-4 text-sm leading-6">
                    <Field label={c.challengeLabel} text={copy.challenge} />
                    <Field label={c.solutionLabel} text={copy.solution} />
                    <Field label={c.resultsLabel} text={copy.results} />
                  </div>

                  <div className="mt-7 flex items-center justify-between border-t border-white/10 pt-5">
                    <span className="text-xs font-medium uppercase tracking-[.12em] text-slate-500">{c.status}</span>
                    <span className="inline-flex items-center gap-2 text-sm text-lilac-200 transition-transform duration-200 group-hover:translate-x-1 rtl:group-hover:-translate-x-1"><ArrowUpRight className="size-4" aria-hidden="true" />{c.viewDetails}</span>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function MetaRow({ label, value }: { label: string; value: string }) {
  return <div className="flex items-start justify-between gap-4"><span className="text-slate-500">{label}</span><span className="text-end text-slate-200">{value}</span></div>;
}

function Field({ label, text }: { label: string; text: string }) {
  return <div><p className="mono-label">{label}</p><p className="mt-1 text-slate-400">{text}</p></div>;
}

function ProjectSignal({ type }: { type: "terrain" | "corridor" | "urban" }) {
  if (type === "terrain") return <div className="project-signal project-signal-lidar" aria-hidden="true">{pointClouds.map((point, index) => <span key={index} style={{ left: point.left, top: point.top, animationDelay: point.delay }} />)}<Layers3 className="project-signal-icon" /></div>;
  if (type === "corridor") return <div className="project-signal project-signal-navigation" aria-hidden="true"><div className="trajectory-line"/><div className="trajectory-dot"/><Route className="project-signal-icon" /><Crosshair className="project-signal-crosshair" /></div>;
  return <div className="project-signal project-signal-segmentation" aria-hidden="true"><div className="segmentation-grid"/><div className="segmentation-frame"/><Satellite className="project-signal-icon" /></div>;
}
